"use client";

import { memo } from "react";
import { flexRender, type Cell, type Row } from "@tanstack/react-table";
import type { RowData } from "@tanstack/react-table";
import type { RecordGridFeatures } from "@/components/ui/record-table/grid/record-grid-features";
import { cn } from "@/lib/utils";
import { RecordRowActions } from "./record-row-actions";
import { useRowOpen } from "./use-row-open";
import { rowMenuHandler } from "./use-row-menu";
import { cellAlignmentClass, cellClassName } from "./column-meta";
import { ACTIONS_COLUMN_WIDTH } from "./grid-metrics";
import type { RowMenuController } from "./use-row-menu";
import type { TableAction } from "../types";

interface RecordGridRowProps<TData extends RowData> {
  row: Row<RecordGridFeatures, TData>;
  href?: string;
  rowActions?: TableAction<TData>[];
  onRowClick?: (row: TData) => void;
  onRowHover?: (id: string | null) => void;
  onRowMenu?: RowMenuController<TData>["onRowMenu"];
  isActive?: boolean;
  isSelected: boolean;
  isExpanded?: boolean;
  className?: string;
  columnWidths: Map<string, number>;
  columnOrderKey: string;
  hasFiller: boolean;
}

function RecordGridRowInner<TData extends RowData>({
  row,
  href,
  rowActions,
  onRowClick,
  onRowHover,
  onRowMenu,
  isActive = false,
  isSelected,
  isExpanded,
  className,
  columnWidths,
  hasFiller,
}: RecordGridRowProps<TData>) {
  const open = useRowOpen({ row: row.original, href, onRowClick, expanded: isExpanded });

  return (
    <tr
      data-state={isSelected ? "selected" : undefined}
      data-active={isActive || undefined}
      {...open.props}
      onContextMenu={rowMenuHandler(row.original, onRowMenu)}
      onMouseEnter={onRowHover ? () => onRowHover(row.id) : undefined}
      onMouseLeave={onRowHover ? () => onRowHover(null) : undefined}
      className={cn(
        "group/row bg-card transition-colors hover:bg-background",
        className,
        isSelected
          ? "bg-selection hover:bg-selection [&>td:first-child]:shadow-[inset_3px_0_0_var(--color-action-fill)]"
          : isActive && "bg-background hover:bg-background",
        open.isInteractive && "cursor-pointer",
      )}
    >
      {row.getVisibleCells().map((cell) => (
        <RecordGridCell key={cell.id} cell={cell} columnWidths={columnWidths} />
      ))}
      {hasFiller ? <td aria-hidden="true" className="border-b border-border" /> : null}
      {rowActions?.length ? <RecordGridActionsCell row={row.original} actions={rowActions} /> : null}
    </tr>
  );
}

function RecordGridActionsCell<TData extends RowData>({ row, actions }: { row: TData; actions: TableAction<TData>[] }) {
  return (
    <td
      style={{ width: ACTIONS_COLUMN_WIDTH }}
      className="sticky right-0 z-10 h-[var(--table-row-height)] border-b border-border bg-inherit px-1 text-center align-middle"
    >
      <div className="opacity-0 transition-opacity group-hover/row:opacity-100 focus-within:opacity-100 [@media(pointer:coarse)]:opacity-100">
        <RecordRowActions row={row} actions={actions} />
      </div>
    </td>
  );
}

function RecordGridCell<TData extends RowData>({
  cell,
  columnWidths,
}: {
  cell: Cell<RecordGridFeatures, TData, unknown>;
  columnWidths: Map<string, number>;
}) {
  const isPinned = cell.column.getIsPinned() === "start";
  return (
    <td
      style={{
        width: columnWidths.get(cell.column.id) ?? cell.column.getSize(),
        ...(isPinned ? { left: cell.column.getStart("start") } : {}),
      }}
      className={cn(
        "h-[var(--table-row-height)] truncate border-b border-border px-[var(--table-cell-px)] py-[var(--table-cell-py)] align-middle [&_.text-sm]:text-[length:var(--table-font-size)]",
        isPinned &&
          "sticky z-10 bg-inherit in-data-[scrolled-x]:shadow-[inset_-1px_0_0_var(--color-border)]",
        cellAlignmentClass(cell.column.columnDef.meta),
        cellClassName(cell.column.columnDef.meta),
      )}
    >
      {flexRender(cell.column.columnDef.cell, cell.getContext())}
    </td>
  );
}

// WHY: React's memo() returns a non-generic component type, and the row's callback props are contravariant in TData, so neither a wrapper nor memo's own typing can carry the generic; the cast restores the signature memo leaves unchanged at runtime.
export const RecordGridRow = memo(
  RecordGridRowInner,
) as typeof RecordGridRowInner;
